import { useState, useCallback } from "react";
import { fetchWithAuth } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { toastHandler } from "@/lib/ToastHandler";
import { getDateRange } from "@/lib/utils";

// Interface para los datos de analytics del tiempo promedio por estado
export interface AverageTimeByStatus {
  status: string;
  average_time_minutes: number;
  count: number;
}

// Interface para los datos de métricas de estado
export interface StateMetrics {
  status: string;
  avg_duration_seconds: number;
  total_orders: number;
  min_duration_seconds: number;
  max_duration_seconds: number;
}

export function useOrdersApi() {
  const [totalOrders, setTotalOrders] = useState<number>(0);
  const [dailyBreakdown, setDailyBreakdown] = useState<
    {
      date: string;
      totalOrders: number;
      completedOrders: number;
      avgCompletionTime: number;
    }[]
  >([]);
  const [stateMetrics, setStateMetrics] = useState<StateMetrics[]>([]);
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { getAccessToken, supabaseInfo } = useAuth();

  // Wrap getAccessToken para añadir manejo de errores
  const getTokenWithErrorHandling = useCallback(async () => {
    try {
      const token = await getAccessToken();
      return token;
    } catch (error) {
      console.error("Error getting access token:", error);
      setError(
        "Error de autenticación. Por favor, intente iniciar sesión nuevamente.",
      );
      throw error;
    }
  }, [getAccessToken]);

  // Obtener métricas de estado
  const fetchStateMetrics = useCallback(
    async (
      brandId?: string | number,
      branchId?: string | number,
      period?: "day" | "week" | "month",
    ) => {
      setLoading(true);
      try {
        const token = await getTokenWithErrorHandling();

        // Obtener rango de fechas basado en el período
        const { fromDate, toDate } = getDateRange(
          period as "day" | "week" | "month",
        );

        // Construir parámetros de consulta
        const params = new URLSearchParams();
        params.append("fromDate", fromDate);
        params.append("toDate", toDate);

        if (brandId && brandId !== "all")
          params.append("brandId", brandId.toString());
        if (branchId && branchId !== "all")
          params.append("branchId", branchId.toString());

        const queryString = params.toString();
        const url = `/order/orders/analytics/state-metrics?${queryString}`;

        const response = await fetchWithAuth(url, token);

        // Si response está vacío, simplemente devuelve los arrays vacíos sin mostrar error
        if (!response || Object.keys(response).length === 0) {
          setStateMetrics([]);
          setDailyBreakdown([]);
          setTotalOrders(0);
          setError(null);
          return [];
        }

        const metricsData =
          response?.data?.stateMetrics || response?.stateMetrics || [];
        const dailyBreakdown =
          response?.summary?.dailyBreakdown || response?.dailyBreakdown || [];

        setStateMetrics(metricsData);
        setDailyBreakdown(dailyBreakdown);
        setTotalOrders(response?.summary?.totalOrders || 0);
        setError(null);
        return metricsData;
      } catch (err) {
        setError("Error al obtener las métricas de estado");
        toastHandler({
          description: "Error al obtener las métricas de estado",
          variant: "destructive",
          title: "Error",
        });
        return [];
      } finally {
        setLoading(false);
      }
    },
    [getTokenWithErrorHandling],
  );

  // Obtener todas las órdenes
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getTokenWithErrorHandling();
      const response = await fetchWithAuth("/order/orders", token);
      // Extraer datos desde response.data o response directamente
      const ordersData = response?.data || response || [];
      const validOrders = Array.isArray(ordersData) ? ordersData : [];

      setOrders(validOrders);
      setError(null);
      return validOrders;
    } catch (err) {
      setError("Error al obtener las órdenes");
      toastHandler({
        description: "Error al obtener las órdenes",
        variant: "destructive",
        title: "Error",
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, [getTokenWithErrorHandling]);

  // Obtener tipos de cancelación
  const fetchCancellationTypes = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getTokenWithErrorHandling();
      const response = await fetchWithAuth("/order/orders/cancel/types", token);

      const list = response?.data || response || [];
      return Array.isArray(list) ? (list as string[]) : [];
    } catch (err) {
      setError("Error al obtener los motivos de cancelación");
      toastHandler({
        description: "Error al obtener los motivos de cancelación",
        variant: "destructive",
        title: "Error",
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, [getTokenWithErrorHandling]);

  // Actualizar estado de una orden
  const updateOrderStatus = useCallback(
    async (
      orderId: number,
      status: string,
      extraPayload?: Record<string, any>,
    ) => {
      setLoading(true);
      try {
        // Validar que orderId sea un número válido
        if (!orderId || isNaN(Number(orderId))) {
          throw new Error(
            `Invalid orderId: ${orderId}. Must be a valid number.`,
          );
        }

        // Validar que status no esté vacío
        if (!status || status.trim() === "") {
          throw new Error("Status cannot be empty");
        }

        const token = await getTokenWithErrorHandling();
        const url =
          status === "cancelled" ? `${orderId}/cancel` : "statusupdate";
        const payload =
          status === "cancelled"
            ? extraPayload
            : {
                orderId: Number(orderId), // Asegurar que sea número
                status: status.trim(), // Limpiar espacios
                userId: supabaseInfo?.id,
              };

        const response = await fetchWithAuth(`/order/orders/${url}`, token, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });

        setError(null);
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Error desconocido al actualizar el estado de la orden";
        setError(errorMessage);
        toastHandler({
          description: errorMessage,
          variant: "destructive",
          title: "Error",
        });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [getTokenWithErrorHandling, supabaseInfo],
  );

  const updateOrderDriver = useCallback(
    async (orderId: number, driverId: number) => {
      setLoading(true);
      try {
        const token = await getTokenWithErrorHandling();
        const response = await fetchWithAuth(
          `/order/orders/${orderId}/assign-driver`,
          token,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "x-user-id": supabaseInfo?.id || "",
            },
            body: JSON.stringify({
              driverId: driverId,
            }),
          },
        );
        setError(null);
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Error desconocido al asignar conductor a la orden";
        setError(errorMessage);
        toastHandler({
          description: errorMessage,
          variant: "destructive",
          title: "Error",
        });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [getTokenWithErrorHandling, supabaseInfo],
  );

  // Obtener historial de estados de una orden
  const fetchOrderStateHistory = useCallback(
    async (orderId: number) => {
      setLoading(true);
      try {
        // Validar que orderId sea un número válido
        if (!orderId || isNaN(Number(orderId))) {
          throw new Error(
            `Invalid orderId: ${orderId}. Must be a valid number.`,
          );
        }

        const token = await getTokenWithErrorHandling();
        const response = await fetchWithAuth(
          `/order/orders/${orderId}/state-history`,
          token,
        );

        // Extraer datos desde response.data o response directamente
        const historyData = response?.stateHistory || response || [];
        const validHistory = Array.isArray(historyData) ? historyData : [];

        setError(null);
        return validHistory;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Error al obtener el historial de estados de la orden";
        setError(errorMessage);
        toastHandler({
          description: errorMessage,
          variant: "destructive",
          title: "Error",
        });
        return [];
      } finally {
        setLoading(false);
      }
    },
    [getTokenWithErrorHandling],
  );

  // Obtener detalle de una orden por ID
  const fetchOrderById = useCallback(
    async (orderId: number) => {
      setLoading(true);
      try {
        if (!orderId || isNaN(Number(orderId))) {
          throw new Error(
            `Invalid orderId: ${orderId}. Must be a valid number.`,
          );
        }

        const token = await getTokenWithErrorHandling();
        const response = await fetchWithAuth(`/order/orders/${orderId}`, token);
        const order = response?.data || response || null;
        return order;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Error al obtener el detalle de la orden";
        setError(errorMessage);
        toastHandler({
          description: errorMessage,
          variant: "destructive",
          title: "Error",
        });
        return null;
      } finally {
        setLoading(false);
      }
    },
    [getTokenWithErrorHandling],
  );

  // Obtener múltiples órdenes por IDs
  const fetchOrdersByIds = useCallback(
    async (ids: (string | number)[]) => {
      const validIds = (ids || [])
        .map((id) => Number(id))
        .filter((id) => !Number.isNaN(id) && id > 0);
      if (validIds.length === 0) return [] as any[];

      try {
        setLoading(true);
        const token = await getTokenWithErrorHandling();
        const settled = await Promise.allSettled(
          validIds.map(async (id) => {
            const res = await fetchWithAuth(`/order/orders/${id}`, token);
            return res?.data || res || null;
          }),
        );
        const fulfilled = settled
          .filter(
            (r): r is PromiseFulfilledResult<any> => r.status === "fulfilled",
          )
          .map((r) => r.value)
          .filter(Boolean);
        const failedCount = settled.filter(
          (r) => r.status === "rejected",
        ).length;
        if (failedCount > 0) {
          toastHandler({
            description: `No se pudieron cargar ${failedCount} de ${validIds.length} órdenes`,
            variant: "destructive",
            title: "Carga parcial",
          });
        }
        return fulfilled;
      } catch (err) {
        setError("Error al obtener las órdenes por IDs");
        toastHandler({
          description: "Error al obtener las órdenes por IDs",
          variant: "destructive",
          title: "Error",
        });
        return [];
      } finally {
        setLoading(false);
      }
    },
    [getTokenWithErrorHandling],
  );

  // Obtener historial de conductores (ordenado: actual primero, luego anteriores)
  const fetchOrderDriversHistory = useCallback(
    async (orderId: number) => {
      setLoading(true);
      try {
        if (!orderId || isNaN(Number(orderId))) {
          throw new Error(
            `Invalid orderId: ${orderId}. Must be a valid number.`,
          );
        }

        const token = await getTokenWithErrorHandling();
        const response = await fetchWithAuth(
          `/assignment/assignments/order/${orderId}/drivers-history`,
          token,
        );

        const history = response || [];
        const list = Array.isArray(history) ? history : response?.data || [];
        return Array.isArray(list) ? list : [];
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Error al obtener el historial de conductores";
        setError(errorMessage);
        toastHandler({
          description: errorMessage,
          variant: "destructive",
          title: "Error",
        });
        return [];
      } finally {
        setLoading(false);
      }
    },
    [getTokenWithErrorHandling],
  );

  // Crear orden manual
  const createManualOrder = useCallback(
    async (orderData: {
      tracking_id: string;
      subtotal: number;
      total_delivery_charge: number;
      discount: number;
      tax: number;
      comment?: string;
      is_paid: boolean;
      currency: string;
      order_type: string;
      public_id: number;
      paid_with: string;
      status: string;
      reception_at: string;
      dispatch_date?: string;
      duration_ms?: number;
      is_external: boolean;
      priority: string;
      organization_code: string; // Add the organization_code field to the interface
      customer: {
        first_name: string;
        first_last_name: string;
        full_name: string;
        is_cell_phone: boolean;
        country_code: string;
        phone: string;
      };
      address: {
        latitude: number;
        longitude: number;
        full_address: string;
        street: string;
        zip_code: string;
        city: string;
      };
      driver_id?: number | null;
      branch_id: number;
    }) => {
      setLoading(true);
      try {
        const token = await getTokenWithErrorHandling();

        // Add ismanual flag to the payload
        const payload = {
          ...orderData,
          is_manual: true,
        };

        const response = await fetchWithAuth("/order/orders", token, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });

        setError(null);
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Error desconocido al crear la orden";
        setError(errorMessage);
        // Check for duplicate tracking ID error and show a friendlier message
        let friendlyMessage = errorMessage;
        if (
          typeof errorMessage === "string" &&
          errorMessage.includes(
            'duplicate key value violates unique constraint "orders_tracking_id_unique"',
          )
        ) {
          friendlyMessage = `Ya existe una orden con este tracking ID "${orderData.tracking_id}". Por favor, verifica el identificador e intenta nuevamente.`;
        } else if (err instanceof TypeError) {
          friendlyMessage =
            "No se pudo conectar con el servidor. Inténtelo de nuevo más tarde.";
        }
        toastHandler({
          description: friendlyMessage,
          variant: "destructive",
          title: "Error al crear la orden",
        });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [getTokenWithErrorHandling],
  );

  return {
    stateMetrics,
    orders,
    loading,
    error,
    fetchStateMetrics,
    fetchOrders,
    fetchCancellationTypes,
    updateOrderStatus,
    updateOrderDriver,
    fetchOrderStateHistory,
    fetchOrderDriversHistory,
    fetchOrderById,
    fetchOrdersByIds,
    createManualOrder,
    totalOrders,
    dailyBreakdown,
  };
}
